import * as React from "react";
import { cn } from "@/lib/utils";

// Re-export AppCard from dedicated file to avoid duplicate definitions
// Consumers can import { AppCard } from either AppCard.tsx or AppPageLayout.tsx
export {
  AppCard,
  AppCardHeader,
  AppCardTitle,
  AppCardDescription,
  AppCardContent,
  AppCardFooter,
  AppCardSection,
} from './AppCard';

/**
 * AppPageLayout - Global Page Layout Wrapper (MANDATORY)
 * 
 * Per Global UI Standardization Enforcement:
 * - All pages MUST be wrapped inside AppPageLayout
 * - No page should directly apply margin/padding manually
 * - Enforces consistent spacing, typography, and structure
 * 
 * Layout Structure:
 * - Page Title (16px, 600 weight)
 * - Sub Header (optional, 12px, 500 weight)
 * - Primary Actions (right aligned)
 * - Content area with consistent padding
 */

interface AppPageLayoutProps {
  children: React.ReactNode;
  className?: string;
  /** Maximum width constraint */
  maxWidth?: "sm" | "md" | "lg" | "xl" | "xxl" | "full";
  /** Remove default padding (for nested layouts) */
  noPadding?: boolean;
}

const maxWidthClasses = {
  sm: "max-w-screen-sm",
  md: "max-w-screen-md",
  lg: "max-w-screen-lg",
  xl: "max-w-[1280px]",
  xxl: "max-w-[1400px]",  // Per Global UI Design System: 1400px max-width option
  full: "max-w-full",
};

export function AppPageLayout({
  children,
  className,
  maxWidth = "xl",
  noPadding = false,
}: AppPageLayoutProps) {
  return (
    <div
      className={cn(
        "w-full mx-auto",
        maxWidthClasses[maxWidth],
        !noPadding && "px-4 py-4",
        "app-page-layout", // For global CSS targeting
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * AppPageHeader - Standardized page header
 * 
 * Structure:
 * - Title (16px, 600 weight)
 * - Description (12px, 400 weight, muted)
 * - Actions (right aligned)
 */
interface AppPageHeaderProps {
  title: string;
  description?: string;
  children?: React.ReactNode; // Action buttons
  className?: string;
}

export function AppPageHeader({
  title,
  description,
  children,
  className,
}: AppPageHeaderProps) {
  return (
    <div className={cn("flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4", className)}>
      <div className="min-w-0">
        {/* Page Title: 16px, 600 weight */}
        <h1 className="app-page-title">{title}</h1>
        {description && (
          /* Description: 12px, muted */
          <p className="app-page-description">{description}</p>
        )}
      </div>
      {children && (
        /* Actions: right aligned with 8px gap */
        <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * AppPageSection - Section container with consistent spacing
 * 
 * Per Global UI: Section vertical spacing = 16px
 */
interface AppPageSectionProps {
  children: React.ReactNode;
  className?: string;
  /** Section title (14px, 600 weight) */
  title?: string;
  /** Section description (12px, muted) */
  description?: string;
}

export function AppPageSection({
  children,
  className,
  title,
  description,
}: AppPageSectionProps) {
  return (
    <div className={cn("mb-4", className)}>
      {(title || description) && (
        <div className="mb-3">
          {title && <h2 className="app-section-title">{title}</h2>}
          {description && <p className="app-body-text text-muted-foreground mt-0.5">{description}</p>}
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * AppFormGrid - Multi-column form layout
 * 
 * Per Global UI Multi-Column Layout:
 * - Screen ≥ 1280px: 2-column grid
 * - Form row gap: 12px
 */
interface AppFormGridProps {
  children: React.ReactNode;
  className?: string;
  columns?: 1 | 2 | 3;
}

export function AppFormGrid({
  children,
  className,
  columns = 2,
}: AppFormGridProps) {
  const columnClasses = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
  };

  return (
    <div className={cn("grid gap-3", columnClasses[columns], className)}>
      {children}
    </div>
  );
}

/**
 * AppFormField - Form field wrapper with consistent spacing
 * 
 * Per Global UI:
 * - Input vertical spacing: 8px
 * - Label: 12px, 500 weight
 */
interface AppFormFieldProps {
  children: React.ReactNode;
  className?: string;
  /** Span full width in grid */
  fullWidth?: boolean;
}

export function AppFormField({
  children,
  className,
  fullWidth = false,
}: AppFormFieldProps) {
  return (
    <div className={cn("space-y-1.5", fullWidth && "md:col-span-2 lg:col-span-3", className)}>
      {children}
    </div>
  );
}

/**
 * AppDashboardGrid - Dashboard card grid
 * 
 * Per Global UI:
 * - Dashboard cards use 3-4 column grid
 */
interface AppDashboardGridProps {
  children: React.ReactNode;
  className?: string;
  columns?: 2 | 3 | 4;
}

export function AppDashboardGrid({
  children,
  className,
  columns = 4,
}: AppDashboardGridProps) {
  const columnClasses = {
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
  };

  return (
    <div className={cn("grid gap-4", columnClasses[columns], className)}>
      {children}
    </div>
  );
}

/**
 * AppStatCard - Dashboard stat card
 */
interface AppStatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  trend?: { value: number; isPositive: boolean };
  /** Color-filled card background class, e.g. 'bg-primary-soft' */
  cardBg?: string;
  className?: string;
}

export function AppStatCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  cardBg,
  className,
}: AppStatCardProps) {
  return (
    <div className={cn(
      "rounded-xl border border-border-hairline p-4 transition-colors duration-150",
      cardBg || "bg-surface-premium",
      className
    )}>
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="app-small-meta text-muted-foreground truncate">{title}</p>
          <p className="app-page-title mt-1.5">{value}</p>
          {subtitle && (
            <p className="app-small-meta text-muted-foreground mt-1">{subtitle}</p>
          )}
          {trend && (
            <p className={cn(
              "app-small-meta mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md",
              trend.isPositive ? "text-success bg-success-soft" : "text-destructive bg-destructive-soft"
            )}>
              {trend.isPositive ? "↑" : "↓"} {Math.abs(trend.value)}%
            </p>
          )}
        </div>
        {icon && (
          <div className="flex-shrink-0 p-2.5 bg-background/50 rounded-lg">
            {icon}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * AppButtonGroup - Button group with consistent spacing
 * 
 * Per Global UI: Button group gap = 8px
 */
interface AppButtonGroupProps {
  children: React.ReactNode;
  className?: string;
  align?: "left" | "center" | "right";
}

export function AppButtonGroup({
  children,
  className,
  align = "left",
}: AppButtonGroupProps) {
  const alignClasses = {
    left: "justify-start",
    center: "justify-center",
    right: "justify-end",
  };

  return (
    <div className={cn("flex flex-wrap items-center gap-2", alignClasses[align], className)}>
      {children}
    </div>
  );
}

/**
 * PageKPISection - Optional KPI/Summary row
 *
 * Global UI Design System:
 * - Sits between Page Header and Main Content
 * - Uses 3-4 column grid for KPI cards
 * - Consistent spacing (space-y-4)
 */
interface PageKPISectionProps {
  children: React.ReactNode;
  className?: string;
}

export function PageKPISection({
  children,
  className,
}: PageKPISectionProps) {
  return (
    <div className={cn("mb-4", className)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {children}
      </div>
    </div>
  );
}

/**
 * PageMainContent - Main content area wrapper
 *
 * Global UI Design System:
 * - Enforces space-y-4 between sections
 * - Contains Cards/Tables
 */
interface PageMainContentProps {
  children: React.ReactNode;
  className?: string;
}

export function PageMainContent({
  children,
  className,
}: PageMainContentProps) {
  return (
    <div className={cn("space-y-4", className)}>
      {children}
    </div>
  );
}

export default AppPageLayout;
